import { useLayoutEffect, useRef, useState } from 'react';
import type {
    CSSProperties,
    KeyboardEvent,
    PointerEvent,
    ReactNode,
} from 'react';
import classNames from 'classnames';
import { useDialogListWidth } from '../model/listWidthStore';
import classes from '../ChatDialogPage.module.scss';

interface ResizableChatPanelsProps {
    list: ReactNode;
    children: ReactNode;
    hasSelection: boolean;
}

/** Размещает список и переписку с ограниченной по ширине подвижной границей */
export const ResizableChatPanels = ({ list, children, hasSelection }: ResizableChatPanelsProps) => {
    const width = useDialogListWidth((state) => state.width);
    const setWidth = useDialogListWidth((state) => state.setWidth);
    const column = useRef<HTMLDivElement>(null);
    const minimum = useRef<HTMLSpanElement>(null);
    const maximum = useRef<HTMLSpanElement>(null);
    const drag = useRef<{ x: number; width: number } | null>(null);

    const [bounds, setBounds] = useState({
        min: 72,
        max: 416
    });

    useLayoutEffect(() => {
        /** Считывает ограничения из текущей темы и состояния навигационной панели */
        const measure = (): void => {
            const min = minimum.current?.getBoundingClientRect().width;
            const max = maximum.current?.getBoundingClientRect().width;

            if (min && max) setBounds({
                min,
                max
            });
        };

        measure();

        const observer = typeof ResizeObserver === 'undefined'
            ? null
            : new ResizeObserver(measure);

        if (minimum.current) observer?.observe(minimum.current);
        if (maximum.current) observer?.observe(maximum.current);
        return () => observer?.disconnect();
    }, []);

    const currentWidth = Math.min(bounds.max, Math.max(bounds.min, width ?? bounds.max));

    /** Ограничивает изменение ширины компактным и исходным размерами списка */
    const resize = (value: number): void => setWidth(Math.min(bounds.max, Math.max(bounds.min, value)));

    /** Начинает перетаскивание с фактической ширины колонки */
    const start = (event: PointerEvent<HTMLDivElement>): void => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.currentTarget.focus();
        event.currentTarget.setPointerCapture(event.pointerId);
        drag.current = {
            x: event.clientX,
            width: column.current?.getBoundingClientRect().width ?? currentWidth
        };
    };

    /** Меняет ширину движением захваченного указателя */
    const move = (event: PointerEvent<HTMLDivElement>): void => {
        if (drag.current) resize(drag.current.width + event.clientX - drag.current.x);
    };

    /** Завершает перетаскивание и освобождает указатель */
    const finish = (event: PointerEvent<HTMLDivElement>): void => {
        drag.current = null;

        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    };

    /** Позволяет менять ширину стрелками и переходить к крайним положениям */
    const handleKey = (event: KeyboardEvent<HTMLDivElement>): void => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();

        if (event.key === 'Home') resize(bounds.min);
        else if (event.key === 'End') resize(bounds.max);
        else resize(currentWidth + (event.key === 'ArrowLeft'
            ? -16
            : 16));
    };

    return <div
        className={classNames(classes.workspace, { [classes.hasSelection]: hasSelection })}
        style={width === null
            ? undefined
            : { '--vox-chat-resized-width': `${width}px` } as CSSProperties
        }
    >
        <span
            ref={minimum}
            className={classes.minWidthProbe}
            aria-hidden="true"
        />
        <span
            ref={maximum}
            className={classes.maxWidthProbe}
            aria-hidden="true"
        />
        <div
            ref={column}
            className={classes.listColumn}
        >
            {list}
        </div>
        <div
            role="separator"
            aria-label="Ширина списка диалогов"
            aria-orientation="vertical"
            aria-valuemin={Math.round(bounds.min)}
            aria-valuemax={Math.round(bounds.max)}
            aria-valuenow={Math.round(currentWidth)}
            tabIndex={0}
            className={classes.resizeHandle}
            title="Потяните, чтобы изменить ширину. Двойной щелчок — исходная ширина"
            onPointerDown={start}
            onPointerMove={move}
            onPointerUp={finish}
            onPointerCancel={finish}
            onLostPointerCapture={() => { drag.current = null; }}
            onKeyDown={handleKey}
            onDoubleClick={() => setWidth(null)}
        />
        {children}
    </div>;
};
